import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Button,
  Grid,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Paper,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  Snackbar,
  Alert,
} from '@mui/material';
import { Edit as EditIcon, LocationOn, School, Interests, Groups, PersonAdd, Check, Close, Home } from '@mui/icons-material';
import { initials } from '../../utils/strings';
import { useAuth } from '../../contexts/AuthContext';
import { apiGetUserById, apiListFriendRequests, apiListFriends, apiAcceptFriendRequest, apiDeclineFriendRequest, apiDeleteFriend, apiUpdateProfile, apiGetMyProfile, apiBanUser, apiSendFriendRequest, apiUploadAvatar } from '../../services/api';
import UserActionsMenu from '../../components/Moderation/UserActionsMenu';
import { toDate } from '../../utils/formatTimeAgo';
import { User } from '../../types';
import { MAX_PROFILE_INTERESTS, MAX_PROFILE_INTEREST_CHARS } from '../../constants';
import { findProfileBlockedWord, validateProfileFormInput } from '../../utils/profileModeration';

const SUPER_ADMIN_EMAIL = 'hl865@drexel.edu';

// Gradient-initial avatar background, matching the Home feed look.
const AVATAR_GRAD = 'linear-gradient(140deg,#FFC24B 0%,#FF7A36 60%,#FF5B2E 100%)';

// Real friends list component uses API

// Mock conversation data
const mockConversations: { [key: string]: Array<{ id: string; text: string; isFromMe: boolean; timestamp: Date }> } = {
  '2': [ // Sarah Johnson
    { id: '1', text: "Hey! How's it going? 👋", isFromMe: true, timestamp: new Date(Date.now() - 3600000) },
    { id: '2', text: "Hi! I'm doing great, thanks! How about you? 😊", isFromMe: false, timestamp: new Date(Date.now() - 3500000) },
    { id: '3', text: "Pretty good! Just working on some assignments 📚", isFromMe: true, timestamp: new Date(Date.now() - 3400000) },
    { id: '4', text: "Same here! What classes are you taking this term?", isFromMe: false, timestamp: new Date(Date.now() - 3300000) },
  ],
  '3': [ // Mike Chen
    { id: '1', text: "Hey Mike! How's the robotics project going?", isFromMe: true, timestamp: new Date(Date.now() - 7200000) },
    { id: '2', text: "It's going really well! We're almost done with the prototype", isFromMe: false, timestamp: new Date(Date.now() - 7100000) },
    { id: '3', text: "That's awesome! Can't wait to see it in action 🤖", isFromMe: true, timestamp: new Date(Date.now() - 7000000) },
  ],
  '4': [ // Emma Wilson
    { id: '1', text: "Hi Emma! Are you going to the business networking event?", isFromMe: true, timestamp: new Date(Date.now() - 10800000) },
    { id: '2', text: "Yes! I'm really excited about it. Are you going too?", isFromMe: false, timestamp: new Date(Date.now() - 10700000) },
    { id: '3', text: "Definitely! Should be a great opportunity to meet people", isFromMe: true, timestamp: new Date(Date.now() - 10600000) },
  ],
};

const FriendsList: React.FC = () => {
  const navigate = useNavigate();
  const [incoming, setIncoming] = useState<any[]>([]);
  const [friends, setFriends] = useState<any[]>([]);
  const [loadingFR, setLoadingFR] = useState(false);
  const [loadingFriends, setLoadingFriends] = useState(false);
  const [pendingAction, setPendingAction] = useState<string | null>(null);

  useEffect(() => { (async () => {
    setLoadingFR(true);
    setLoadingFriends(true);
    try {
      const [r, f] = await Promise.all([apiListFriendRequests('incoming'), apiListFriends()]);
      setIncoming(r.items);
      setFriends(f.items);
    } finally {
      setLoadingFR(false);
      setLoadingFriends(false);
    }
  })(); }, []);

  async function onAccept(id: string) {
    setPendingAction(id+':accept');
    const acceptedRequest = incoming.find((r) => r.id === id);
    try {
      await apiAcceptFriendRequest(id);
      setIncoming(prev => prev.filter(r => r.id !== id));

      const acceptedUser = acceptedRequest?.from;
      if (acceptedUser?.id) {
        setFriends(prev => {
          if (prev.some((friend) => friend.id === acceptedUser.id)) return prev;
          return [{ ...acceptedUser, since: new Date().toISOString() }, ...prev];
        });
      }
    } catch (error) {
      console.error('Failed to accept friend request', error);
    } finally {
      setPendingAction(null);
    }
  }
  async function onDecline(id: string) {
    setPendingAction(id+':decline');
    try { await apiDeclineFriendRequest(id); setIncoming(prev => prev.filter(r => r.id !== id)); } finally { setPendingAction(null); }
  }
  async function onRemoveFriend(userId: string) {
    setPendingAction('del:'+userId);
    try { await apiDeleteFriend(userId); setFriends(prev => prev.filter(f => f.id !== userId)); } finally { setPendingAction(null); }
  }

  const openUserProfile = (userId?: string | null) => {
    if (!userId) return;
    navigate(`/profile/${userId}`);
  };

  return <Box>
    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Friend Requests ({incoming.length})</Typography>
    <Box>
      {incoming.map(req => { const u = req.from; const name = u.displayName || `${u.firstName||''} ${u.lastName||''}`.trim() || 'Student'; return (
        <Card key={req.id} variant="outlined" sx={{ mb: 1 }}>
          <CardContent sx={{ py: 1.25 }}>
            <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <Box
                sx={{ display:'flex', alignItems:'center', gap:2, cursor: 'pointer' }}
                onClick={() => openUserProfile(u?.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    openUserProfile(u?.id);
                  }
                }}
              >
                <Avatar src={u.avatarUrl || undefined} sx={{ width:40, height:40, background: AVATAR_GRAD, color:'#fff', fontWeight:700 }}>{(name[0]||'S').toUpperCase()}</Avatar>
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold">{name}</Typography>
                  <Typography variant="body2" color="text.secondary">{u.major} {u.year ? `• Year ${u.year}`: ''}</Typography>
                </Box>
              </Box>
              <Box sx={{ display:'flex', gap:1 }}>
                <IconButton size="small" color="success" disabled={pendingAction===req.id+':accept'} onClick={() => onAccept(req.id)}><Check fontSize="small" /></IconButton>
                <IconButton size="small" color="error" disabled={pendingAction===req.id+':decline'} onClick={() => onDecline(req.id)}><Close fontSize="small" /></IconButton>
              </Box>
            </Box>
          </CardContent>
        </Card>
      ); })}
      {!incoming.length && !loadingFR && <Typography variant="body2" color="text.secondary">No pending requests</Typography>}
    </Box>
    <Typography variant="subtitle2" color="text.secondary" sx={{ mt:3 }} gutterBottom>Friends ({friends.length})</Typography>
    <Box>
      {friends.map(f => { const name = f.displayName || `${f.firstName||''} ${f.lastName||''}`.trim() || 'Student'; return (
        <Card key={f.id} variant="outlined" sx={{ mb:1 }}>
          <CardContent sx={{ py:1.25 }}>
            <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <Box
                sx={{ display:'flex', alignItems:'center', gap:2, cursor: 'pointer' }}
                onClick={() => openUserProfile(f?.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    openUserProfile(f?.id);
                  }
                }}
              >
                <Avatar src={f.avatarUrl || undefined} sx={{ width:40, height:40, background: AVATAR_GRAD, color:'#fff', fontWeight:700 }}>{(name[0]||'S').toUpperCase()}</Avatar>
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold">{name}</Typography>
                  <Typography variant="body2" color="text.secondary">{f.major} {f.year?`• Year ${f.year}`:''}</Typography>
                </Box>
              </Box>
              <Box sx={{ display:'flex', gap:1 }}>
                <Button variant="outlined" size="small" onClick={() => navigate(`/messages/${f.id}`)}>Message</Button>
                <IconButton size="small" color="error" disabled={pendingAction==='del:'+f.id} onClick={() => onRemoveFriend(f.id)}><Close fontSize="small" /></IconButton>
              </Box>
            </Box>
          </CardContent>
        </Card>
      ); })}
      {!friends.length && !loadingFriends && <Typography variant="body2" color="text.secondary">No friends yet</Typography>}
    </Box>
  </Box>;
};

const Profile: React.FC = () => {
  const { id: routeId, userId } = useParams<{ id?: string; userId?: string }>();
  const paramId = routeId || userId;
  const location = useLocation();
  const navigate = useNavigate();
  const { user: me, updateProfile: updateAuthProfile } = useAuth();
  const [viewUser, setViewUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);

  const handleAvatarFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setAvatarUploading(true);
    setAvatarError(null);
    try {
      const { url } = await apiUploadAvatar(file);
      setViewUser((prev) => (prev ? { ...prev, profilePicture: url } : prev));
      updateAuthProfile({ profilePicture: url } as any);
    } catch (err: any) {
      const code = err?.info?.error || err?.message;
      setAvatarError(
        code === 'file_too_large' ? 'Image must be under 5 MB.'
          : code === 'unsupported_file_type' ? 'Use a PNG, JPG, WEBP, or GIF.'
          : 'Upload failed. Please try again.'
      );
    } finally {
      setAvatarUploading(false);
    }
  };
  const [editData, setEditData] = useState<{
    bio?: string;
    major?: string;
    year?: number;
    location?: string;
    hometown?: string;
    interests?: string[];
  }>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [interestInput, setInterestInput] = useState('');
  const [banInProgress, setBanInProgress] = useState(false);
  const [banError, setBanError] = useState<string | null>(null);
  const [friendReqStatus, setFriendReqStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [friendReqMsg, setFriendReqMsg] = useState<string | null>(null);
  // Server-reported relationship with this profile (friends / sent / received / none);
  // drives which friend action button renders instead of always showing "Add Friend".
  const [relationship, setRelationship] = useState<{ status: string; requestId?: string } | null>(null);
  const [reportToast, setReportToast] = useState<string | null>(null);

  const handleAddFriend = async () => {
    if (!profileUser?.id || friendReqStatus === 'sending' || friendReqStatus === 'sent') return;
    setFriendReqStatus('sending');
    setFriendReqMsg(null);
    try {
      await apiSendFriendRequest(profileUser.id);
      setFriendReqStatus('sent');
      setRelationship({ status: 'sent' });
      setFriendReqMsg('Friend request sent');
    } catch (e: any) {
      setFriendReqStatus('error');
      const code = e?.message;
      if (code === 'already_friends') setRelationship({ status: 'friends' });
      setFriendReqMsg(
        code === 'already_friends' ? "You're already friends" :
        code === 'already_pending' ? 'Request already pending' :
        'Could not send friend request'
      );
    }
  };

  const handleAcceptFriend = async () => {
    if (!relationship?.requestId || friendReqStatus === 'sending') return;
    setFriendReqStatus('sending');
    setFriendReqMsg(null);
    try {
      await apiAcceptFriendRequest(relationship.requestId);
      setRelationship({ status: 'friends' });
      setFriendReqStatus('idle');
      setFriendReqMsg('You are now friends');
    } catch {
      setFriendReqStatus('error');
      setFriendReqMsg('Could not accept the request');
    }
  };

  // Fetch when id/userId param is provided; otherwise load /me
  useEffect(() => {
    let cancelled = false;
    const fetchUser = async () => {
      try {
        setLoading(true);
        setError(null);
        setRelationship(null);
        setFriendReqStatus('idle');
        setFriendReqMsg(null);
    if (!paramId) {
      const meResp = await apiGetMyProfile();
      const u = meResp?.data ?? meResp?.user ?? meResp;
          const normalized: User = {
            id: u.id,
            email: u.email,
            firstName: u.firstName,
            lastName: u.lastName,
            displayName: u.displayName,
            profilePicture: u.profileImage ?? u.profilePicture,
            major: u.major ?? me?.major,
            minor: u.minor ?? me?.minor,
            year: Number(u.year ?? me?.year) || 0,
            hometown: u.hometown ?? me?.hometown ?? '',
            location: u.location ?? me?.location ?? '',
            interests: u.interests ?? me?.interests ?? [],
            clubs: u.clubs ?? me?.clubs ?? [],
            courses: u.courses ?? me?.courses ?? [],
            bio: u.bio ?? me?.bio ?? '',
            friendsCount: u.friendsCount ?? (u.friends?.length ?? 0),
            createdAt: toDate(u.createdAt) || new Date(),
            updatedAt: toDate(u.updatedAt) || new Date(),
          };
          if (!cancelled) setViewUser(normalized);
          return;
        }
  const resp = await apiGetUserById(paramId);
        const u = resp?.data ?? resp;
        if (!cancelled) setRelationship((u as any).relationship ?? null);
        const normalized: User = {
          id: u.id,
          email: u.email,
          firstName: u.firstName,
          lastName: u.lastName,
          displayName: u.displayName,
          profilePicture: u.profileImage ?? u.profilePicture,
          major: u.major,
          minor: u.minor,
          year: Number(u.year) || 0,
          hometown: u.hometown ?? '',
          interests: u.interests ?? [],
          clubs: u.clubs ?? [],
          courses: u.courses ?? [],
          bio: u.bio ?? '',
          friendsCount: u.friendsCount ?? (u.friends?.length ?? 0),
          createdAt: toDate(u.createdAt) || new Date(),
          updatedAt: toDate(u.updatedAt) || new Date(),
        };
        if (!cancelled) setViewUser(normalized);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load profile');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchUser();
    return () => { cancelled = true; };
  }, [paramId, me]);

  const profileUser = useMemo(() => (paramId ? viewUser : (viewUser ?? me)), [paramId, viewUser, me]);

  // Null-safe locals (60-second safeguards)
  const interests = profileUser?.interests ?? [];
  const clubs = profileUser?.clubs ?? [];
  // These are optional in some payloads; default to [] to avoid undefined.length
  const courses = profileUser?.courses ?? [];
  const isMe = (!paramId) || (!!me?.id && !!paramId && me.id === paramId) || (!!me?.id && !!profileUser?.id && me.id === profileUser.id);
  const isSuperAdmin = String(me?.email || '').toLowerCase() === SUPER_ADMIN_EMAIL;

  const handleBanUser = async () => {
    if (!profileUser?.id || isMe || !isSuperAdmin || banInProgress) return;
    const name = profileUser.displayName || `${profileUser.firstName || ''} ${profileUser.lastName || ''}`.trim() || 'this user';
    const confirmed = window.confirm(`Ban ${name}? This will permanently remove all their posts, comments/replies, messages, and memberships.`);
    if (!confirmed) return;

    setBanError(null);
    setBanInProgress(true);
    try {
      await apiBanUser(profileUser.id);
      navigate('/search');
    } catch (e: any) {
      setBanError(e?.message || 'Failed to ban user');
    } finally {
      setBanInProgress(false);
    }
  };

  const handleEditClick = () => {
    setSaveError(null);
    setInterestInput('');
    setEditData({
      bio: profileUser?.bio || '',
      major: profileUser?.major || '',
      year: profileUser?.year || 1,
      location: profileUser?.location || '',
      hometown: profileUser?.hometown || '',
      interests: profileUser?.interests || [],
    });
    setIsEditing(true);
  };

  useEffect(() => {
    if (!isMe || isEditing) return;
    const params = new URLSearchParams(location.search);
    if (params.get('edit') !== '1') return;
    // For self-profile onboarding deep links, wait until /profile/me is loaded
    // instead of opening from potentially partial auth-context data.
    if (!paramId && !viewUser) return;
    if (!profileUser) return;
    setSaveError(null);
    setInterestInput('');
    setEditData({
      bio: profileUser.bio || '',
      major: profileUser.major || '',
      year: profileUser.year || 1,
      location: profileUser.location || '',
      hometown: profileUser.hometown || '',
      interests: profileUser.interests || [],
    });
    setIsEditing(true);

    // Consume the one-time onboarding query flag so Cancel/Save can close normally.
    params.delete('edit');
    navigate(
      {
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : '',
      },
      { replace: true },
    );
  }, [isEditing, isMe, location.pathname, location.search, navigate, paramId, profileUser, viewUser]);

  const buildInterestsForSave = (): string[] | null => {
    const existing = Array.isArray(editData.interests) ? editData.interests : [];
    const pending = interestInput.trim();
    if (!pending) return existing;

    if (pending.length > MAX_PROFILE_INTEREST_CHARS) {
      setSaveError(`Each interest must be ${MAX_PROFILE_INTEREST_CHARS} characters or fewer.`);
      return null;
    }

    const blockedInPending = findProfileBlockedWord([pending]);
    if (blockedInPending) {
      setSaveError(`Interest contains a blocked word: ${blockedInPending}`);
      return null;
    }

    const hasPendingAlready = existing.some((item) => item.toLowerCase() === pending.toLowerCase());
    if (hasPendingAlready) return existing;

    if (existing.length >= MAX_PROFILE_INTERESTS) {
      setSaveError(`You can add up to ${MAX_PROFILE_INTERESTS} interests.`);
      return null;
    }

    return [...existing, pending];
  };

  const handleSave = async () => {
    const finalizedInterests = buildInterestsForSave();
    if (!finalizedInterests) return;

    const validationError = validateProfileFormInput({
      bio: editData.bio,
      interests: finalizedInterests,
    });
    if (validationError) {
      setSaveError(validationError);
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      const payload = { ...editData, interests: finalizedInterests };
      const updateResp = await apiUpdateProfile(payload);
      const updatedRaw = updateResp?.user ?? updateResp?.data ?? updateResp;
      const normalized: User = {
        id: updatedRaw.id,
        email: updatedRaw.email,
        firstName: updatedRaw.firstName,
        lastName: updatedRaw.lastName,
        displayName: updatedRaw.displayName,
        profilePicture: updatedRaw.profileImage ?? updatedRaw.profilePicture,
        major: updatedRaw.major,
        minor: updatedRaw.minor,
        year: Number(updatedRaw.year) || 0,
        hometown: updatedRaw.hometown ?? '',
        location: updatedRaw.location ?? '',
        interests: updatedRaw.interests ?? [],
        clubs: updatedRaw.clubs ?? [],
        courses: updatedRaw.courses ?? [],
        bio: updatedRaw.bio ?? '',
        friendsCount: updatedRaw.friendsCount ?? (updatedRaw.friends?.length ?? 0),
        createdAt: toDate(updatedRaw.createdAt) || new Date(),
        updatedAt: toDate(updatedRaw.updatedAt) || new Date(),
      };
      setViewUser(normalized);
      await updateAuthProfile(normalized);
      setInterestInput('');
      setIsEditing(false);
    } catch (error: any) {
      setSaveError(error?.message || 'Failed to save profile changes.');
      console.error('Failed to update profile:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditData({});
    setSaveError(null);
    setInterestInput('');
  };

  const addInterest = () => {
    const next = interestInput.trim();
    if (!next) return;

    if (next.length > MAX_PROFILE_INTEREST_CHARS) {
      setSaveError(`Each interest must be ${MAX_PROFILE_INTEREST_CHARS} characters or fewer.`);
      return;
    }

    const blockedInInterest = findProfileBlockedWord([next]);
    if (blockedInInterest) {
      setSaveError(`Interest contains a blocked word: ${blockedInInterest}`);
      return;
    }

    const existing = editData.interests || [];
    if (existing.some((item) => item.toLowerCase() === next.toLowerCase())) {
      setInterestInput('');
      return;
    }

    if (existing.length >= MAX_PROFILE_INTERESTS) {
      setSaveError(`You can add up to ${MAX_PROFILE_INTERESTS} interests.`);
      return;
    }

    setSaveError(null);
    setEditData({ ...editData, interests: [...existing, next] });
    setInterestInput('');
  };

  const removeInterest = (interest: string) => {
    setEditData({
      ...editData,
      interests: (editData.interests || []).filter((item) => item !== interest),
    });
  };

  if (loading && !profileUser) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6">Loading profile…</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6">{error}</Typography>
      </Box>
    );
  }

  if (!profileUser) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6">User not found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: 1040, mx: 'auto' }}>
      {/* Profile Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3 }}>
            <Avatar
              src={profileUser.profilePicture || undefined}
              sx={{
                width: 100,
                height: 100,
                background: AVATAR_GRAD,
                color: '#fff',
                fontWeight: 700,
                fontSize: '2rem',
                boxShadow: '0 8px 22px -8px rgba(255,106,61,0.55)',
              }}
            >
              {initials(profileUser.displayName || `${profileUser.firstName} ${profileUser.lastName}`)}
            </Avatar>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.1 }} gutterBottom>
                {profileUser.displayName}
              </Typography>
              <Typography variant="body1" color="text.secondary" gutterBottom>
                {profileUser.email}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <School fontSize="small" color="action" />
                <Typography variant="body2">
                  {[profileUser.major, profileUser.year ? `Year ${profileUser.year}` : null].filter(Boolean).join(' • ') || 'No academic info set'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <LocationOn fontSize="small" color="action" />
                <Typography variant="body2">
                  {profileUser.location || 'No location set'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Home fontSize="small" color="action" />
                <Typography variant="body2">
                  {profileUser.hometown ? `From ${profileUser.hometown}` : 'No hometown set'}
                </Typography>
              </Box>
            </Box>
            {isMe ? (
              <IconButton aria-label="Edit profile" onClick={handleEditClick} color="primary">
                <EditIcon />
              </IconButton>
            ) : (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {relationship?.status === 'friends' ? (
                  <Button type="button" variant="outlined" disabled sx={{ '&.Mui-disabled': { color: 'text.secondary', borderColor: 'divider' } }}>
                    ✓ Friends
                  </Button>
                ) : relationship?.status === 'received' ? (
                  <Button type="button" variant="contained" onClick={handleAcceptFriend} disabled={friendReqStatus === 'sending'}>
                    {friendReqStatus === 'sending' ? 'Accepting…' : 'Accept Request'}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="contained"
                    onClick={handleAddFriend}
                    disabled={!profileUser?.id || friendReqStatus === 'sending' || friendReqStatus === 'sent' || relationship?.status === 'sent'}
                  >
                    {relationship?.status === 'sent' || friendReqStatus === 'sent' ? 'Request Sent' : friendReqStatus === 'sending' ? 'Sending…' : 'Add Friend'}
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => navigate(`/messages/${profileUser?.id}`)}
                  disabled={!profileUser?.id}
                >
                  Message
                </Button>
                {isSuperAdmin && (
                  <Button
                    type="button"
                    variant="outlined"
                    color="error"
                    onClick={handleBanUser}
                    disabled={!profileUser?.id || banInProgress}
                  >
                    {banInProgress ? 'Banning...' : 'Ban User'}
                  </Button>
                )}
                {profileUser?.id && (
                  <UserActionsMenu
                    targetUserId={profileUser.id}
                    targetUserName={profileUser.displayName}
                    report={{ targetType: 'user', targetId: profileUser.id }}
                    onBlocked={() => navigate('/search')}
                    onReported={() => setReportToast('Report submitted. Thanks for helping keep the community safe.')}
                    size="medium"
                  />
                )}
              </Box>
            )}
          </Box>

          {profileUser.bio && (
            <Typography variant="body1" sx={{ mb: 2 }}>
              {profileUser.bio}
            </Typography>
          )}
          {banError && (
            <Typography variant="body2" color="error">{banError}</Typography>
          )}
          {friendReqMsg && (
            <Typography variant="body2" color={friendReqStatus === 'error' ? 'error' : 'success.main'}>{friendReqMsg}</Typography>
          )}
        </CardContent>
      </Card>

      {/* Profile Details */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
                <Interests color="primary" />
                Interests
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {interests.map((interest) => (
                  <Chip key={interest} label={interest} size="small" color="primary" variant="outlined" />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
                <Groups color="primary" />
                Clubs & Organizations
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {clubs.map((club) => (
                  <Chip 
                    key={club} 
                    label={club} 
                    size="small" 
                    variant="outlined"
                    clickable
                    onClick={() => navigate(`/clubs?club=${encodeURIComponent(club)}`)}
                  />
                ))}
                {clubs.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No clubs joined yet. <Button size="small" onClick={() => navigate('/clubs')}>Browse clubs</Button>
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                  Courses
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<School />}
                  onClick={() => navigate('/courses')}
                >
                  Browse Courses
                </Button>
              </Box>
              {courses.length > 0 ? (
                <Grid container spacing={2}>
                  {courses.map((course) => (
                    <Grid item xs={12} sm={6} md={4} key={course.id}>
                      <Card 
                        variant="outlined" 
                        sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                        onClick={() => navigate('/courses', { state: { openCourseId: course.id } })}
                      >
                        <CardContent>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {course.code}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {course.name}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No courses joined yet. <Button size="small" onClick={() => navigate('/courses')}>Browse courses</Button>
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Friends Section (only on my profile) */}
        {isMe && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                    Friends
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PersonAdd />}
                    onClick={() => navigate('/search')}
                  >
                    Add Friends
                  </Button>
                </Box>
                <FriendsList />
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Edit Profile Dialog */}
      <Dialog open={isEditing} onClose={handleCancel} maxWidth="md" fullWidth>
        <DialogTitle>Edit Profile</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar src={profileUser.profilePicture || undefined} sx={{ width: 72, height: 72, background: AVATAR_GRAD, color: '#fff', fontWeight: 700 }}>
                  {initials(profileUser.displayName || `${profileUser.firstName} ${profileUser.lastName}`)}
                </Avatar>
                <Box>
                  <Button variant="outlined" component="label" disabled={avatarUploading}>
                    {avatarUploading ? 'Uploading…' : 'Upload photo'}
                    <input hidden type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={handleAvatarFile} />
                  </Button>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    PNG, JPG, WEBP or GIF · up to 5 MB
                  </Typography>
                  {avatarError && <Typography variant="caption" color="error" sx={{ display: 'block' }}>{avatarError}</Typography>}
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Bio"
                multiline
                rows={3}
                value={editData.bio || ''}
                onChange={(e) => setEditData({ ...editData, bio: e.target.value })}
                placeholder="Tell us about yourself..."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Major"
                value={editData.major || ''}
                onChange={(e) => setEditData({ ...editData, major: e.target.value })}
                placeholder="e.g., Computer Science"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Year</InputLabel>
                <Select
                  value={editData.year || 1}
                  label="Year"
                  onChange={(e) => setEditData({ ...editData, year: e.target.value as number })}
                >
                  <MenuItem value={1}>Year 1</MenuItem>
                  <MenuItem value={2}>Year 2</MenuItem>
                  <MenuItem value={3}>Year 3</MenuItem>
                  <MenuItem value={4}>Year 4</MenuItem>
                  <MenuItem value={5}>Year 5</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Current Location"
                value={editData.location || ''}
                onChange={(e) => setEditData({ ...editData, location: e.target.value })}
                placeholder="e.g., Philadelphia, PA"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Hometown"
                value={editData.hometown || ''}
                onChange={(e) => setEditData({ ...editData, hometown: e.target.value })}
                placeholder="e.g., New York, NY"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Interests"
                value={interestInput}
                onChange={(e) => {
                  setInterestInput(e.target.value);
                  if (saveError) setSaveError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault();
                    addInterest();
                  }
                }}
                placeholder="Type an interest and press Enter"
                helperText={`Press Enter to add up to ${MAX_PROFILE_INTERESTS} interests (${MAX_PROFILE_INTEREST_CHARS} chars max each)`}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button onClick={addInterest} disabled={!interestInput.trim()} size="small">
                        Add
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                {(editData.interests || []).map((interest) => (
                  <Chip
                    key={interest}
                    label={interest}
                    onDelete={() => removeInterest(interest)}
                    size="small"
                    color="primary"
                    variant="filled"
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
          {saveError && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              {saveError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      {!isMe && profileUser?.id && (
        <Paper elevation={3} sx={{ position: 'fixed', left: 0, right: 0, bottom: 56, p: 1.5, display: { xs: 'block', md: 'none' }, zIndex: 1200 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" color="primary" fullWidth onClick={() => navigate(`/messages/${profileUser.id}`)}>
              Message {profileUser.displayName?.split(' ')[0] || 'User'}
            </Button>
            {isSuperAdmin && (
              <Button variant="outlined" color="error" onClick={handleBanUser} disabled={banInProgress}>
                Ban
              </Button>
            )}
          </Box>
        </Paper>
      )}

      <Snackbar
        open={!!reportToast}
        autoHideDuration={4000}
        onClose={() => setReportToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: { xs: 80 } }}
      >
        <Alert severity="success" variant="filled" onClose={() => setReportToast(null)} sx={{ width: '100%' }}>
          {reportToast}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Profile; 